import { useEffect, useMemo, useState } from 'react';
import { useStoreActions, useStoreState } from '../models/RootStore';
import { getSiteBacnetUnits } from './useBacnetDevicesApi';
import { getSiteModbusUnitsWithDevices } from './useModbusDevicesApi';
import { useIsModbusEnabled } from '../screens/Modbus/modbusFeature';
import { isControllableBacnetUnit, isControllableModbusUnit } from '../utils/universalUnit/controllable';

const EMPTY_SITE_UNITS = { bacnet: {}, modbus: {} };

/**
 * The site's BACnet/Modbus units that can be added to a group (`groupableUnits`), keyed by id and tagged with
 * `isUniversalUnit` and their `protocol`. BACnet/Modbus units aren't part of allUnits, so they are fetched here.
 */
export const useSiteUniversalUnitsForGroups = (siteId?: string | null) => {
  const { enableBacnet } = useStoreState((s) => s.config);
  const enableModbus = useIsModbusEnabled();
  const { addMessage } = useStoreActions((a) => a.errorMessage);

  const [siteUnits, setSiteUnits] = useState<{ bacnet: any; modbus: any }>(EMPTY_SITE_UNITS);
  // The site whose units are in `siteUnits`, so a site switch counts as loading right away
  const [loadedSiteId, setLoadedSiteId] = useState<string | null>(null);
  const isLoading = !!siteId && loadedSiteId !== siteId;

  useEffect(() => {
    setSiteUnits(EMPTY_SITE_UNITS);
    if (!siteId) {
      return;
    }
    Promise.all([
      enableBacnet ? getSiteBacnetUnits(siteId) : {},
      enableModbus ? getSiteModbusUnitsWithDevices(siteId) : {},
    ])
      .then(([bacnet, modbus]: any) => setSiteUnits({ bacnet: bacnet ?? {}, modbus: modbus ?? {} }))
      .catch((err: any) => addMessage({ message: err.message }))
      .finally(() => setLoadedSiteId(siteId));
  }, [siteId, enableBacnet, enableModbus]);

  const groupableUnits = useMemo(() => {
    const result: Record<string, any> = {};
    const addUnit = (unit: any, protocol: 'bacnet' | 'modbus') => {
      if (!unit?.id) {
        return;
      }
      result[unit.id] = {
        ...unit,
        name: unit.name || unit.title,
        site: unit.site || siteId,
        isUniversalUnit: true,
        protocol,
      };
    };
    // Only offer what Control lists under "Ungrouped BACnet Devices" / "Ungrouped Modbus Devices"
    Object.values(siteUnits.bacnet).forEach((unit: any) => {
      if (isControllableBacnetUnit(unit) && unit.isVisible) {
        addUnit(unit, 'bacnet');
      }
    });
    Object.values(siteUnits.modbus).forEach((unit: any) => {
      if (isControllableModbusUnit(unit)) {
        addUnit(unit, 'modbus');
      }
    });
    return result;
  }, [siteUnits, siteId]);

  return { groupableUnits, isLoading };
};
